import { Injectable } from "@angular/core";
import {
  BehaviorSubject,
  catchError,
  map,
  Observable,
  of,
  Subject,
  takeUntil,
  tap,
  withLatestFrom,
} from "rxjs";
import {
  AcademicInfo,
  AppLoadError,
  AppSubmitError,
  ESchoolLevel,
  IAcademicInfo,
  ISchoolInfo,
  Pal,
  SchoolInfo,
} from "../shared/models";
import { AcademicInfoService } from "../shared/services/api/academic-info.service";
import { ApplicantInfoStoreService } from "./applicant-info-store.service";
import { HttpErrorResponse } from "@angular/common/http";
import { isAnyPropEmpty } from "../utils";
import { SharedStoreService } from "./shared-store.service";
import { GtmService } from "../shared/services/gtm.service";

@Injectable({
  providedIn: "root",
})
export class AcademicInfoStoreService {
  private _academicInfo = new BehaviorSubject<IAcademicInfo>(null);
  private _loading = new BehaviorSubject<boolean>(false);
  private _error = new BehaviorSubject<AppLoadError | null>(null);
  private _saving = new BehaviorSubject<boolean>(false);

  // Observables for components to subscribe to
  academicInfo$: Observable<IAcademicInfo> = this._academicInfo.asObservable();
  loading$: Observable<boolean> = this._loading.asObservable();
  error$: Observable<AppLoadError | null> = this._error.asObservable();
  saving$: Observable<boolean> = this._saving.asObservable();

  destroyed$ = new Subject();

  // get current value
  get academicInfo() {
    return this._academicInfo.getValue();
  }

  constructor(
    private service: AcademicInfoService,
    private applicantInfoStore: ApplicantInfoStoreService,
    private sharedStore: SharedStoreService,
    private gtmService: GtmService
  ) {}

  selectAcademicInfo(): Observable<IAcademicInfo> {
    return this.academicInfo$.pipe(map((academicInfo) => academicInfo));
  }

  // Select the loading state
  selectLoading(): Observable<boolean> {
    return this.loading$.pipe(map((loading) => loading));
  }

  // Select the error state
  selectError(): Observable<AppLoadError | null> {
    return this.error$.pipe(map((error) => error));
  }

  selectSaving(): Observable<boolean> {
    // Provide a method to check saving state
    return this.saving$.pipe(map((saving) => saving));
  }

  selectLatest(): Observable<{
    id: string;
    highSchool: ISchoolInfo;
    postSecondaryEdus: ISchoolInfo[];
  } | null> {
    return this.academicInfo$.pipe(
      map((academicInfo) => {
        if (academicInfo) {
          const { id, schoolsAttended = [] } = academicInfo;
          let highSchool: ISchoolInfo;
          let postSecondaryEdus: ISchoolInfo[];

          if (schoolsAttended.length === 0) {
            highSchool = new SchoolInfo();
            postSecondaryEdus = [];
          } else {
            highSchool =
              schoolsAttended.find(
                (edu) => edu.level === ESchoolLevel.HIGH_SCHOOL
              ) || new SchoolInfo();
            postSecondaryEdus = schoolsAttended.filter(
              (edu) => edu.level !== ESchoolLevel.HIGH_SCHOOL
            );
          }

          return {
            id,
            highSchool,
            postSecondaryEdus,
          };
        }

        return null;
      })
    );
  }

  selectAcademicInfoReview(): Observable<
    {
      level: ESchoolLevel;
      name: string;
    }[]
  > {
    return this.academicInfo$.pipe(
      map((academicInfo) => {
        return (
          academicInfo?.schoolsAttended.map((school) => ({
            level: school.level,
            name: school.name,
          })) || null
        );
      })
    );
  }

  /**
   * @GET
   */
  loadAcademicInfo() {
    this._loading.next(true);

    return this.service.getAcademicInfo().pipe(
      withLatestFrom(this.applicantInfoStore.selectApplicationID()),
      map(([academicInfo, appId]) => {
        if (!academicInfo || Object.entries(academicInfo).length === 0) {
          this._academicInfo.next(new AcademicInfo(appId));
          return new AcademicInfo(appId);
        } else {
          academicInfo.id = appId;
          this._academicInfo.next(academicInfo);

          return academicInfo;
        }
      }),
      catchError((err: HttpErrorResponse) => {
        const error = err instanceof HttpErrorResponse ? err.error : err;
        const displayError = error
          ? new AppLoadError(error.id, error.message)
          : new AppLoadError();

        return of(displayError);
      }),
      tap(() => this._loading.next(false)),
      takeUntil(this.destroyed$)
    );
  }
  /**
   * @POST
   */
  postAcademicInfo(payload: IAcademicInfo) {
    this._saving.next(true);
    payload.schoolsAttended = payload.schoolsAttended.reduce(
      (formatted: ISchoolInfo[], schoolInfo: ISchoolInfo) => {
        if (!isAnyPropEmpty(schoolInfo)) {
          formatted.push(schoolInfo);
        }
        return formatted;
      },
      []
    );

    this._academicInfo.next(payload);
    return this.service.postAcademicInfo(payload).pipe(
      map(() => {
        this.gtmService.pushTag({
          event: "international_application_progress",
          step_number: "5",
          step_name: "academic-info",
        });

        this.sharedStore.triggerNavigationAfterSubmit("ACADEMIC_INFO");
        return payload;
      }),
      catchError((err: HttpErrorResponse) => {
        const error = err instanceof HttpErrorResponse ? err.error : err;

        const displayError = error
          ? new AppSubmitError(error.id, error.message)
          : new AppSubmitError();
        this._error.next(error);
        return of(displayError);
      }),
      tap(() => this._saving.next(false)),
      takeUntil(this.destroyed$)
    );
  }

  clearError(): void {
    this._error.next(null);
  }

  ngOnDestroy() {
    this.destroyed$.next(0);
    this.destroyed$.unsubscribe();
  }
}
